import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { AddPhotoAlternate } from "@mui/icons-material";
import { Box, Modal } from "@mui/material";
import clsx from 'clsx';
import { useDrag, useDrop } from 'react-dnd';
import { useFormik } from 'formik';
import "react-datepicker/dist/react-datepicker.css";
import Cropper from "react-easy-crop";
import CreatableSelect from 'react-select/creatable';
import ReactSelect from 'react-select';

import { getEmptyImage } from 'react-dnd-html5-backend';
import * as Yup from 'yup';
import { KTSVG, isNotEmpty, toAbsoluteUrl } from '../../../../../../_metronic/helpers';
import { RadioInput, convertArrToSelect, dateDownloadFormat, downloadFile } from "../../../../../../jsFunctions";
import { TemplatesListLoading } from '../components/loading/TemplatesListLoading';
import { useListView } from '../core/ListViewProvider';
import { useQueryResponse } from '../core/QueryResponseProvider';
import { Template, initialTemplate } from '../core/_models';
import { PrintingTemplateOption, updateTemplate } from '../core/_requests';

import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";


interface DragItem {
  index: number;
}

interface DragCollectedProps {
  isDragging: boolean;
}

type Props = {
  isTemplateLoading: boolean
  template: Template
  printingTemplates: PrintingTemplateOption[]
  isPrintingTemplatesLoading?: boolean
  printingTemplatesError?: string
}

const editTemplateSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Name is required'),
})


interface DropdownOptions {
  readonly value: string;
  readonly label: string;
}

interface PrintableTemplatePreviewState {
  avatar: string
  label: string
  value: string
  top: number
  left: number
}

const dropdownOptions: readonly DropdownOptions[] = [
  { value: 'template', label: 'template' },
  { value: 'client', label: 'client' },
  { value: 'testing', label: 'testing' },
  { value: 'SAYCHEESE', label: 'SAYCHEESE' },
];

const ReorderableImage = ({
  face,
  index,
  reorderImages,
  removeImage,
  replaceImage,
  setCropModalOpen,
  setCurrentImage,
  setCurrentIndex,
}: {
  face: string;
  index: number;
  reorderImages: (dragIndex: number, dropIndex: number) => void;
  removeImage: (index: number) => void;
  replaceImage: (index: number, file: File) => void;
  setCropModalOpen: (open: boolean) => void;
  setCurrentImage: (image: string | null) => void;
  setCurrentIndex: (index: number | null) => void;
}) => {
  const dragItemRef = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: "image",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "image",
    drop: (item: { index: number }) => {
      const dragIndex = item.index;
      const dropIndex = index;

      if (dragIndex !== dropIndex) {
        reorderImages(dragIndex, dropIndex);
      }
    },
  });

  useEffect(() => {
    if (dragItemRef.current) {
      preview(dragItemRef.current, { captureDraggingState: true });
    }
  }, [preview]);

  const handleReupload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      replaceImage(index, file);
      const reader = new FileReader();
      reader.onload = () => {
        setCurrentImage(reader.result as string); // Open the crop modal with the newly uploaded image
        setCropModalOpen(true);
        setCurrentIndex(index); // Track the index for replacement
      };
      reader.readAsDataURL(file);
      event.target.value = ''
    }
  };

  return (
    <div
      ref={(node) => {
        drag(drop(node));
        dragItemRef.current = node;
      }}
      style={{
        position: "relative",
        width: "150px",
        height: "225px",
        borderRadius: "5px",
        overflow: "hidden",
        boxShadow: "0 1px 11px rgba(0, 0, 0, 0.1)",
        opacity: isDragging ? 0.5 : 1,
        transition: "opacity 0.2s ease",
        cursor: "move",
      }}
    >
      <img
        src={face}
        alt={`Uploaded ${index}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onClick={() => document.getElementById(`reupload-input-${index}`)?.click()} // Trigger reupload input click
      />
      <input
        type="file"
        accept="image/*"
        id={`reupload-input-${index}`}
        style={{ display: "none" }}
        onChange={handleReupload}
      />
      <div
        onClick={() => removeImage(index)}
        className="svg-icon-2 text-danger btn-danger"
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          width: "40px",
          height: "40px",
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          borderRadius: "10%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
        }}
        data-bs-toggle="tooltip"
        title="Delete Image"
      >
        <KTSVG
          path="/media/icons/duotune/general/gen027.svg"
          className="svg-icon-2 text-danger"
        />
      </div>
    </div>
  );
};

const TemplateEditModalForm: FC<Props> = ({
  template,
  isTemplateLoading,
  printingTemplates,
  isPrintingTemplatesLoading = false,
  printingTemplatesError = '',
}) => {
  const printableTemplateEditorBaseUrl = 'https://saycheese-editor.web.app/'
  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  // alert(template.disable + " " + template.dooh)

  const templateForEdit = useMemo<Template>(() => {
    const rawPrintableTemplateId =
      typeof template.printableTemplateId === 'string' ? template.printableTemplateId.trim() : ''
    const printableTemplateId =
      !rawPrintableTemplateId ||
      rawPrintableTemplateId.toLowerCase() === 'null' ||
      rawPrintableTemplateId.toLowerCase() === 'undefined'
        ? ''
        : rawPrintableTemplateId

    return {
      ...template,
      avatar: template.avatar || initialTemplate.avatar,
      dooh: template.dooh || initialTemplate.dooh,
      uploadAllowed: template.uploadAllowed || initialTemplate.uploadAllowed,
      microsite: template.microsite || initialTemplate.microsite,
      printableTemplateId,
      info: template.info || initialTemplate.info,
      name: template.name || initialTemplate.name,
      tags: template.tags ? convertArrToSelect(template.tags) : dropdownOptions,
      faces: template.faces || [], // Ensure faces are initialized here
      facesFiles: template.facesFiles || [], // Ensure facesFiles are initialized here
    }
  }, [template])

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const blankImg = toAbsoluteUrl('/media/svg/avatars/blank.svg')

  const [avatar, setAvatar] = useState<any>(templateForEdit.avatar);
  useEffect(() => {
    setAvatar(templateForEdit.avatar)
  }, [templateForEdit.avatar])

  // const handleChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ): Promise<any> => {

  //   const ImageName = event.target.value.split('\\')[2];
  //   const Image = event.target.value;
  //   console.log('Imageon Kevel: ', Image); // Image on Kevel
  //   console.log('ImageName on Kevel: ', ImageName); // ImageName on Kevel
  //   console.log('ImageLink on Kevel: ', event.target.value); // ImageLink on Kevel

  //   if (event?.target?.files?.[0]!) {
  //     setAvatar({ display: URL.createObjectURL(event?.target?.files?.[0]), uploadAllowed: event?.target?.files?.[0] });
  //   }
  // };

  const formik = useFormik({
    initialValues: templateForEdit,
    enableReinitialize: true,
    validationSchema: editTemplateSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updateTemplate({ ...values })
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(true)
        cancel(true)
      }
    },
  })

  const normalizedPrintableTemplateId = typeof formik.values.printableTemplateId === 'string'
    ? formik.values.printableTemplateId.trim()
    : ''
  const printableTemplateEditorUrl = normalizedPrintableTemplateId
    ? `${printableTemplateEditorBaseUrl}?templateId=${encodeURIComponent(normalizedPrintableTemplateId)}`
    : printableTemplateEditorBaseUrl

  const printableTemplateSelectKey = `${templateForEdit.id || 'new'}-${normalizedPrintableTemplateId || 'empty'}`

  const selectedPrintingTemplate = useMemo(
    () => {
      if (!normalizedPrintableTemplateId) {
        return null
      }

      return printingTemplates.find((item) => item.value === normalizedPrintableTemplateId) || null
    },
    [printingTemplates, normalizedPrintableTemplateId]
  )
  const [printableTemplatePreview, setPrintableTemplatePreview] =
    useState<PrintableTemplatePreviewState | null>(null)
  const printableTemplatePreviewShowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const printableTemplatePreviewHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearPrintableTemplatePreviewTimers = () => {
    if (printableTemplatePreviewShowTimeoutRef.current) {
      clearTimeout(printableTemplatePreviewShowTimeoutRef.current)
      printableTemplatePreviewShowTimeoutRef.current = null
    }

    if (printableTemplatePreviewHideTimeoutRef.current) {
      clearTimeout(printableTemplatePreviewHideTimeoutRef.current)
      printableTemplatePreviewHideTimeoutRef.current = null
    }
  }

  const schedulePrintableTemplatePreviewHide = () => {
    if (printableTemplatePreviewShowTimeoutRef.current) {
      clearTimeout(printableTemplatePreviewShowTimeoutRef.current)
      printableTemplatePreviewShowTimeoutRef.current = null
    }

    if (printableTemplatePreviewHideTimeoutRef.current) {
      clearTimeout(printableTemplatePreviewHideTimeoutRef.current)
    }

    printableTemplatePreviewHideTimeoutRef.current = setTimeout(() => {
      setPrintableTemplatePreview(null)
      printableTemplatePreviewHideTimeoutRef.current = null
    }, 160)
  }

  const showPrintableTemplatePreview = (
    event: React.MouseEvent<HTMLElement>,
    option: PrintingTemplateOption
  ) => {
    if (!option.avatar) {
      return
    }

    if (printableTemplatePreviewHideTimeoutRef.current) {
      clearTimeout(printableTemplatePreviewHideTimeoutRef.current)
      printableTemplatePreviewHideTimeoutRef.current = null
    }

    if (printableTemplatePreviewShowTimeoutRef.current) {
      clearTimeout(printableTemplatePreviewShowTimeoutRef.current)
    }

    const target = event.currentTarget
    printableTemplatePreviewShowTimeoutRef.current = setTimeout(() => {
      const targetRect = target.getBoundingClientRect()
      const previewWidth = 300
      const previewHeight = 440
      const viewportPadding = 20
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let left = targetRect.left + targetRect.width / 2
      let top = targetRect.top + targetRect.height / 2

      if (left + previewWidth / 2 > viewportWidth - viewportPadding) {
        left = viewportWidth - previewWidth / 2 - viewportPadding
      } else if (left - previewWidth / 2 < viewportPadding) {
        left = previewWidth / 2 + viewportPadding
      }

      if (top + previewHeight / 2 > viewportHeight - viewportPadding) {
        top = viewportHeight - previewHeight / 2 - viewportPadding
      } else if (top - previewHeight / 2 < viewportPadding) {
        top = previewHeight / 2 + viewportPadding
      }

      setPrintableTemplatePreview({
        avatar: option.avatar || '',
        label: option.label,
        value: option.value,
        top,
        left,
      })

      printableTemplatePreviewShowTimeoutRef.current = null
    }, 200)
  }

  useEffect(() => {
    return () => {
      if (printableTemplatePreviewShowTimeoutRef.current) {
        clearTimeout(printableTemplatePreviewShowTimeoutRef.current)
      }
      if (printableTemplatePreviewHideTimeoutRef.current) {
        clearTimeout(printableTemplatePreviewHideTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!formik.values.print && printableTemplatePreview) {
      setPrintableTemplatePreview(null)
    }
  }, [formik.values.print, printableTemplatePreview])

  useEffect(() => {
    const rawPrintableTemplateId =
      typeof template.printableTemplateId === 'string' ? template.printableTemplateId.trim() : ''

    if (
      !rawPrintableTemplateId ||
      rawPrintableTemplateId.toLowerCase() === 'null' ||
      rawPrintableTemplateId.toLowerCase() === 'undefined'
    ) {
      if (formik.values.printableTemplateId !== '') {
        formik.setFieldValue('printableTemplateId', '', false)
      }
    }
  }, [template.id, template.printableTemplateId])

  const handleMicrositeChange = (newValue: boolean) => {
    formik.setFieldValue('microsite', newValue);
    if (newValue) {
      formik.setFieldValue('uploadAllowed', true);
    }
  };

  const handleDOOHChange = (newValue: boolean) => {
    formik.setFieldValue('dooh', newValue);
    if (newValue) {
      formik.setFieldValue('uploadAllowed', true);
    }
  };

  const handleUploadChange = (newValue: boolean) => {
    if (newValue) {
      formik.setFieldValue('uploadAllowed', newValue);
    }
    else if (!formik.values.microsite && !formik.values.dooh) {
      formik.setFieldValue('uploadAllowed', newValue);
    }
  };

  const removeImage = (index: number) => {
    const updatedFaces = [...(formik.values.faces || [])];
    updatedFaces.splice(index, 1);
    formik.setFieldValue('faces', updatedFaces);
  };

  const replaceImage = (index: number, file: File) => {
    const updatedFaces = [...(formik.values.faces || [])];
    const imageURL = URL.createObjectURL(file);
    updatedFaces[index] = imageURL; // Replace the image URL
    formik.setFieldValue('faces', updatedFaces);

    const updatedFiles = [...(formik.values.facesFiles || [])];
    updatedFiles[index] = file; // Replace the actual file
    formik.setFieldValue('facesFiles', updatedFiles);
  };

  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropConfig, setCropConfig] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [currentIndex, setCurrentIndex] = useState<number | null>(0); // Add this line to keep track of the current image's index

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setCurrentImage(imageURL);
      setCropModalOpen(true);
      event.target.value = ''
    }
  };

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const confirmCrop = async () => {
    if (!currentImage || !croppedAreaPixels || currentIndex === null) return;

    const croppedImage = await getCroppedImage(currentImage, croppedAreaPixels);

    // Safely access `formik.values.faces` as an array
    const updatedFaces = [...(formik.values.faces || [])];
    updatedFaces[currentIndex] = croppedImage;

    const updatedFiles = [...(formik.values.facesFiles || [])];
    updatedFiles[currentIndex] = base64ToFile(croppedImage);

    formik.setFieldValue("faces", updatedFaces);
    formik.setFieldValue("facesFiles", updatedFiles);

    setCropModalOpen(false);
    setCurrentImage(null);
    setCurrentIndex(null);
  };

  const getCroppedImage = (imageSrc: string, croppedAreaPixels: any) => {
    return new Promise<string>((resolve) => {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
          img,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        resolve(canvas.toDataURL("image/jpeg"));
      };
    });
  };

  const base64ToFile = (base64: string): File => {
    const byteString = atob(base64.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uintArray[i] = byteString.charCodeAt(i);
    }

    // Add timestamp to filename to ensure uniqueness
    const uniqueName = `cropped_${Date.now()}_${Math.random().toString(36).substring(2, 9)}.jpg`;

    const blob = new Blob([uintArray], { type: 'image/jpeg' });
    return new File([blob], uniqueName, { type: 'image/jpeg' });
  };

  const reorderImages = (dragIndex: number, dropIndex: number) => {
    const updatedFaces = [...(formik.values.faces || [])];
    const updatedFiles = [...(formik.values.facesFiles || [])];

    const [movedFace] = updatedFaces.splice(dragIndex, 1);
    const [movedFile] = updatedFiles.splice(dragIndex, 1);

    updatedFaces.splice(dropIndex, 0, movedFace);
    updatedFiles.splice(dropIndex, 0, movedFile);

    formik.setFieldValue('faces', updatedFaces);
    formik.setFieldValue('facesFiles', updatedFiles);
  };

  return (
    <>
      <form id='kt_modal_add_template_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_template_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_template_header'
          data-kt-scroll-wrappers='#kt_modal_add_template_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='d-block fw-bold fs-6 mb-5'>Template Cover</label>
            {/* end::Label */}

            {/* begin::Image input */}
            <div
              // className='image-input image-input-outline'
              className='image-input'
              data-kt-image-input='true'
              style={{ backgroundImage: `url('${blankImg}')` }}
            >
              {/* begin::Preview existing avatar */}


              {templateForEdit.video ? (
                <>
                  <video
                    className='content-input-wrapper'
                    src={templateForEdit.video}
                    controls
                    controlsList="nodownload"
                    style={{
                      width: '225px',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '10px 10px 10px 10px',
                    }}
                  />
                  <a
                    onClick={() => {
                      downloadFile(
                        templateForEdit.video,
                        dateDownloadFormat(templateForEdit.name) +
                        ".mp4",
                        true
                      )
                    }}
                    download={`${templateForEdit.id}.mp4`}
                    className='btn btn-icon btn-sm btn-bg-white btn-active-color-primary position-absolute top-0 end-0 m-3 mt-5'
                    data-bs-toggle='tooltip'
                    data-bs-placement='top'
                    title='Download Video'
                  >
                    <i className='bi bi-download'></i>
                  </a>
                </>
              ) : (
                <>
                  <img
                    className='image-input-wrapper'
                    src={avatar.display ? avatar.display : templateForEdit.avatar}
                    alt='Image Alt Text'
                    style={{
                      width: '125px',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '10px 10px 10px 10px',
                    }}
                  />
                  <a
                    onClick={() => {
                      downloadFile(avatar.display ? avatar.display : templateForEdit.avatar, dateDownloadFormat(templateForEdit.name))
                    }}
                    download={`${templateForEdit.name}.jpg`}
                    className='btn btn-icon btn-sm btn-bg-white btn-active-color-primary position-absolute bottom-0 end-0 m-3'
                    data-bs-toggle='tooltip'
                    data-bs-placement='top'
                    title='Download Image'
                  >
                    <i className='bi bi-download'></i>
                  </a>
                </>
              )}

              {/* end::Preview existing avatar */}

              {/* begin::Cancel */}
              {/* <span
              className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
              data-kt-image-input-action='cancel'
              data-bs-toggle='tooltip'
              title='Cancel avatar'
            >
              <i className='bi bi-x fs-2'></i>
            </span> */}
              {/* end::Cancel */}

              {/* begin::Remove */}
              {/* <span
                className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                data-kt-image-input-action='remove'
                data-bs-toggle='tooltip'
                title='Remove avatar'
              >
                <i className='bi bi-x fs-2'></i>
              </span> */}
              {/* end::Remove */}
            </div>
            {/* end::Image input */}

            {/* begin::Hint */}
            {/* <div className='form-text'>Allowed file types: png, jpg, jpeg.</div> */}
            {/* end::Hint */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>Template Title</label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder='Template Title'
              {...formik.getFieldProps('name')}
              type='text'
              name='name'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.name && formik.errors.name },
                {
                  'is-valid': formik.touched.name && !formik.errors.name,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isTemplateLoading}
            />
            {formik.touched.name && formik.errors.name && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.name}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>Date Created</label>
            {/* begin::Input */}
            <input
              placeholder='Date Created'
              aria-disabled
              {...formik.getFieldProps('dateCreated')}
              type='text'
              name='dateCreated'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.dateCreated && formik.errors.dateCreated },
                {
                  'is-valid': formik.touched.dateCreated && !formik.errors.dateCreated,
                }
              )}
              autoComplete='off'
              disabled={true}
            />
            {formik.touched.dateCreated && formik.errors.dateCreated && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.dateCreated}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}


          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>Last Modified</label>
            {/* begin::Input */}
            <input
              placeholder='Last Moditied'
              aria-disabled
              {...formik.getFieldProps('updatedOn')}
              type='text'
              name='updatedOn'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.updatedOn && formik.errors.updatedOn },
                {
                  'is-valid': formik.touched.updatedOn && !formik.errors.updatedOn,
                }
              )}
              autoComplete='off'
              disabled={true}
            />
            {formik.touched.updatedOn && formik.errors.updatedOn && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.updatedOn}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}


          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>Dimensions (w by h)</label>
            {/* begin::Input */}
            <input
              placeholder='Dimensions'
              aria-disabled
              {...formik.getFieldProps('dimensions')}
              type='text'
              name='dimensions'
              value={
                formik.values.dimensions
                  ? `${formik.values.dimensions.width} by ${formik.values.dimensions.height}`
                  : ''
              }
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.dimensions && formik.errors.dimensions },
                { 'is-valid': formik.touched.dimensions && !formik.errors.dimensions }
              )}
              autoComplete='off'
              disabled={true}
            />
            {formik.touched.dimensions && formik.errors.dimensions && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.dimensions}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          {
            templateForEdit.video &&
            <>
              {/* begin::Input group */}
              <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Thumbnail Time (1000ms = 1s)</label>
                {/* begin::Input */}
                <input
                  placeholder='Thumbnail capture timestamp in milliseconds'
                  aria-disabled
                  {...formik.getFieldProps('thumbnailMark')}
                  type='text'
                  name='thumbnailMark'
                  value={
                    formik.values.thumbnailMark
                  }
                  className={clsx(
                    'form-control form-control-solid mb-3 mb-lg-0',
                    { 'is-invalid': formik.touched.thumbnailMark && formik.errors.thumbnailMark },
                    {
                      'is-valid': formik.touched.thumbnailMark && !formik.errors.thumbnailMark,
                    }
                  )}
                  autoComplete='off'
                  disabled={false}
                />
                {formik.touched.thumbnailMark && formik.errors.thumbnailMark && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.thumbnailMark}</span>
                    </div>
                  </div>
                )}
                {/* end::Input */}
              </div>
              {/* end::Input group */}
            </>
          }



          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>Id</label>
            {/* begin::Input */}
            <input
              placeholder='Id'
              aria-disabled
              {...formik.getFieldProps('Id')}
              type='text'
              name='id'
              value={
                formik.values.id

              }
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.id && formik.errors.id },
                { 'is-valid': formik.touched.id && !formik.errors.id }
              )}
              autoComplete='off'
              disabled={true}
            />
            {formik.touched.id && formik.errors.id && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.id}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>Tags</label>
            {/* end::Label */}

            <div
              className='px-2'
              onClick={(event) => {
                event.stopPropagation();
              }}>

              <CreatableSelect
                // value={value}
                {...formik.getFieldProps('tags')}
                placeholder="Type to Choose or Create"
                hideSelectedOptions={false}
                onChange={(newValue) => {
                  formik.setFieldValue('tags', newValue, false)
                }}
                backspaceRemovesValue={false}
                blurInputOnSelect={false}
                className='text-uppercase fs-8'
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 0,
                  colors: {
                    ...theme.colors,
                  },
                })}
                isMulti={true}
                styles={{
                  option: (base, state) => ({
                    ...base,
                  }),
                  multiValue: (styles, { data }) => {
                    return {
                      ...styles,
                      borderRadius: 5,
                      backgroundColor: '#432cffb8',
                      color: 'white',
                      boxShadow: '#0303030f 0px 0px 20px 0px'
                    };
                  },
                  multiValueLabel: (styles, { data }) => ({
                    ...styles,
                    fontWeight: 'bold',
                    color: 'white'

                  }),
                  multiValueRemove: (styles, { data }) => ({
                    ...styles,
                    ':hover': {
                      color: 'gray',
                    },
                  }),
                  control: (base) => ({
                    ...base,
                    // backgroundColor: var(--kt-input-solid-bg),
                    // zIndex: 9999,
                    padding: "5px",
                    border: "none",
                    borderRadius: 10,
                    boxShadow: '#3d88a614 0px 0px 20px 0px'
                    // boxShadow: 'rgba(0, 0, 0, 0.1) 0px 10px 50px'
                    // boxShadow: "0 0 50px 0 rgb(82 63 105 / 15%)",
                  }),
                  menu: (base) => ({
                    ...base,
                    // zIndex: 19999,
                    backgroundColor: "white",
                    padding: "10px",
                    border: "none",
                    borderRadius: 10,
                    boxShadow: '#3d88a614 0px 0px 20px 0px'
                    // boxShadow: 'rgba(0, 0, 0, 0.07) -4px 9px 25px -6px'
                    // boxShadow: 'rgba(0, 0, 0, 0.1) 0px 10px 50px',
                    // boxShadow: "0 0 50px 0 rgb(82 63 105 / 15%)",
                  }),
                  singleValue: (base) => ({ ...base, color: "#000", borderRadius: 10, }),
                }}
                // defaultValue={[dropdownOptions[2], dropdownOptions[3]]}
                isDisabled={false}
                isLoading={false}
                isClearable={true}
                isRtl={false}
                isSearchable={true}
                name="color"
                options={dropdownOptions}
              />

              {formik.touched.tags && formik.errors.tags && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.tags}</span>
                  </div>
                </div>
              )}
            </div>
            {/* end::Input */}
          </div>

          {formik.values.print && (
            <div className='fv-row mb-7'>
              <div className='d-flex justify-content-between align-items-center px-2 mb-2'>
                <label className='required fw-bold fs-6 mb-0'>Printable Template</label>
                <a
                  className='btn btn-light-primary'
                  href={printableTemplateEditorUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {normalizedPrintableTemplateId ? 'Edit Template' : 'Create New'}
                </a>
              </div>
              <div
                className='px-2'
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <ReactSelect
                  key={printableTemplateSelectKey}
                  value={selectedPrintingTemplate}
                  placeholder='Tap or Search'
                  hideSelectedOptions={false}
                  onChange={(newValue) => {
                    formik.setFieldValue('printableTemplateId', newValue?.value || '', false)
                  }}
                  filterOption={(option, rawInput) => {
                    const normalizedInput = rawInput.toLowerCase().trim()
                    if (!normalizedInput) {
                      return true
                    }

                    const searchArray = [
                      ...(option.data.tags || []),
                      option.label,
                      option.value,
                    ]
                      .filter((value) => typeof value === 'string')
                      .map((value: string) => value.toLowerCase())

                    return searchArray.some((value: string) => value.includes(normalizedInput))
                  }}
                  backspaceRemovesValue={false}
                  blurInputOnSelect={false}
                  className='fs-6'
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                    colors: {
                      ...theme.colors,
                    },
                  })}
                  styles={{
                    control: (base) => ({
                      ...base,
                      padding: "5px",
                      border: "none",
                      borderRadius: 10,
                      boxShadow: '#3d88a614 0px 0px 20px 0px',
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "white",
                      padding: "10px",
                      border: "none",
                      borderRadius: 10,
                      boxShadow: '#3d88a614 0px 0px 20px 0px',
                    }),
                    singleValue: (base) => ({ ...base, color: "#000", borderRadius: 10 }),
                  }}
                  formatOptionLabel={(option) => (
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-50px overflow-hidden me-3'>
                        {option.avatar ? (
                          <div
                            className='symbol-label'
                            onMouseEnter={(event) => showPrintableTemplatePreview(event, option)}
                            onMouseLeave={schedulePrintableTemplatePreviewHide}
                            style={{ cursor: 'zoom-in' }}
                          >
                            <img src={option.avatar} alt={option.label} className='w-100' />
                          </div>
                        ) : (
                          <div className='symbol-label fs-3 bg-light-primary text-primary'>
                            {option.label?.[0]}
                          </div>
                        )}
                      </div>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 text-hover-primary mb-1'>{option.label}</span>
                        <span style={{ color: 'black' }}>{option.value}</span>
                      </div>
                    </div>
                  )}
                  isDisabled={formik.isSubmitting || isTemplateLoading}
                  isLoading={isPrintingTemplatesLoading}
                  isClearable={true}
                  isRtl={false}
                  isSearchable={true}
                  name='printableTemplateId'
                  options={printingTemplates}
                  noOptionsMessage={() => {
                    if (isPrintingTemplatesLoading) {
                      return 'Loading printable templates...'
                    }
                    if (printingTemplatesError) {
                      return 'Failed to load printable templates'
                    }
                    return 'No printable templates found'
                  }}
                />
              </div>
              <div className='form-text px-2 mt-2'>
                {normalizedPrintableTemplateId
                  ? 'The editor will load your selected printable template.'
                  : 'No printable template selected. This opens a blank editor so you can create one.'}
              </div>
              {printingTemplatesError && (
                <div className='fv-plugins-message-container px-2 mt-2'>
                  <div className='fv-help-block'>
                    <span role='alert'>Printable templates error: {printingTemplatesError}</span>
                  </div>
                </div>
              )}
              {!printingTemplatesError && !isPrintingTemplatesLoading && printingTemplates.length > 0 && (
                <div className='form-text px-2 mt-2'>
                  Loaded {printingTemplates.length} printable templates.
                </div>
              )}
              {!printingTemplatesError && !isPrintingTemplatesLoading && printingTemplates.length === 0 && (
                <div className='form-text px-2 mt-2'>
                  No documents found in `printingTemplate`, `printingTemplates`, or `templates`.
                </div>
              )}
              {selectedPrintingTemplate?.scenePath && (
                <div className='form-text px-2 mt-2'>
                  Scene: {selectedPrintingTemplate.scenePath}
                </div>
              )}
            </div>
          )}

          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className=' fw-bold fs-6 mb-2'>Extra Info</label>
            {/* end::Label */}

            {/* begin::Input */}
            <textarea
              className='form-control form-control-solid mb-8'
              rows={3}
              placeholder='Add extra info here'
              {...formik.getFieldProps('info')}
              name='info'
              autoComplete='off'
              disabled={formik.isSubmitting || isTemplateLoading}
            ></textarea>
            {/* end::Input */}
          </div>

          <div className='mb-7'>
            <label className='required fw-bold fs-6 mb-5'>Hide on LUCY</label>
            <div className='d-flex fv-row'>
              <RadioInput
                formik={formik}
                name='disable'
                label='Yes'
                value={true}
                id='kt_modal_update_role_option_0'
                isTemplateLoading={isTemplateLoading}
              />
            </div>
            <div className='separator separator-dashed my-5'></div>
            <div className='d-flex fv-row'>
              <RadioInput
                formik={formik}
                name='disable'
                label='No'
                value={false}
                id='kt_modal_update_role_option_1'
                isTemplateLoading={isTemplateLoading}
              />
            </div>
            <div className='separator separator-dashed my-5'></div>
          </div>

          <div className='mb-7'>
            <label className='required fw-bold fs-6 mb-5'>Type</label>
            <div className='d-flex fv-row'>
              <RadioInput
                formik={formik}
                name='type'
                label='Classic'
                value={'classic'}
                id='kt_modal_update_role_option_0'
                isTemplateLoading={isTemplateLoading}
              />
            </div>
            <div className='separator separator-dashed my-5'></div>
            <div className='d-flex fv-row'>
              <RadioInput
                formik={formik}
                name='type'
                label='Ai'
                value={'ai'}
                id='kt_modal_update_role_option_1'
                isTemplateLoading={isTemplateLoading}
              />
            </div>
            <div className='separator separator-dashed my-5'></div>
            <div className='d-flex fv-row'>
              <RadioInput
                formik={formik}
                name='type'
                label='Magic'
                value={'magic'}
                id='kt_modal_update_role_option_1'
                isTemplateLoading={isTemplateLoading}
              />
            </div>
            <div className='separator separator-dashed my-5'></div>
            <div className='d-flex fv-row'>
              <RadioInput
                formik={formik}
                name='type'
                label='Sticker'
                value={'sticker'}
                id='kt_modal_update_role_option_1'
                isTemplateLoading={isTemplateLoading}
              />
            </div>
            <div className='separator separator-dashed my-5'></div>
          </div>

          <div className='mb-7 d-flex justify-content-between align-items-center'>
            <div className='fw-bold fs-6.25'>Storywall</div>
            <div className='form-check form-check-custom form-check-solid form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='storywallSwitch'
                checked={formik.values.microsite}
                onChange={() => handleMicrositeChange(!formik.values.microsite)}
                // onChange={() => formik.setFieldValue('microsite', !formik.values.microsite)}
                disabled={isTemplateLoading || formik.values.type === 'sticker'}
              />
            </div>
          </div>


          <div className='mb-7 d-flex justify-content-between align-items-center'>
            <div className='fw-bold fs-6.25'>DOOH</div>
            <div className='form-check form-check-custom form-check-solid form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='doohSwitch'
                checked={formik.values.dooh}
                onChange={() => handleDOOHChange(!formik.values.dooh)}
                // onChange={() => formik.setFieldValue('dooh', !formik.values.dooh)}
                disabled={isTemplateLoading}
              />
            </div>
          </div>


          <div className='mb-7 d-flex justify-content-between align-items-center'>
            <div className='fw-bold fs-6.25'>Print</div>
            <div className='form-check form-check-custom form-check-solid form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='printSwitch'
                checked={formik.values.print}
                onChange={() => formik.setFieldValue('print', !formik.values.print)}
                disabled={isTemplateLoading || formik.values.type === 'sticker'}
              />
            </div>
          </div>

          
          <div className='mb-7 d-flex justify-content-between align-items-center'>
            <div className='fw-bold fs-6.25'>Upload to Dashboard</div>
            <div className='form-check form-check-custom form-check-solid form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='uploadAllowedSwitch'
                checked={formik.values.uploadAllowed}
                onChange={() => handleUploadChange(!formik.values.uploadAllowed)}
                // onChange={() => formik.setFieldValue('uploadAllowed', !formik.values.uploadAllowed)}
                disabled={isTemplateLoading || formik.values.microsite || formik.values.dooh}
              />
            </div>
          </div>
          {/* begin::Input group */}
          <>
            {formik.values.type === 'ai' && (
              <>
                <label className="fw-bold fs-6 mb-5">Faces?</label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: '10px',
                    padding: '10px',
                  }}
                >
                  {/* Upload Area */}
                  <div
                    style={{
                      width: '150px',
                      height: '225px',
                      border: '2px dashed #eff2f9',
                      borderRadius: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      document.getElementById('image-upload-input')?.click()

                      setCurrentIndex(formik.values.faces?.length ? formik.values.faces?.length : 0);
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      id="image-upload-input"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                    />
                    <AddPhotoAlternate style={{ fontSize: '36px', color: '#eff2f9' }} />
                  </div>

                  {/* Uploaded Images */}
                  {formik.values.faces?.map((face, index) => (
                    <ReorderableImage
                      key={index}
                      face={face}
                      index={index}
                      reorderImages={reorderImages}
                      removeImage={removeImage}
                      replaceImage={replaceImage}
                      setCropModalOpen={setCropModalOpen}
                      setCurrentImage={setCurrentImage}
                      setCurrentIndex={setCurrentIndex}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Crop Modal */}
            {cropModalOpen && (
              <Modal
                open={cropModalOpen}
                onClose={() => setCropModalOpen(false)}
                closeAfterTransition
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90vw",
                    maxWidth: 800,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    animation: "fadeIn 0.3s ease-in-out",
                    "@keyframes fadeIn": {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                  }}
                >
                  {currentImage && (
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: 400,
                        background: "#f0f0f0",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "1px solid #ddd",
                      }}
                    >
                      <Cropper
                        image={currentImage}
                        crop={cropConfig}
                        zoom={zoom}
                        aspect={2 / 3}
                        onCropChange={setCropConfig}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                      />
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", width: "100%" }}>
                    <Tooltip title="Cancel and close the modal" arrow>
                      <button
                        style={{
                          backgroundColor: "white",
                          color: "#007bff",
                          padding: "10px 20px",
                          border: "1px solid #007bff",
                          borderRadius: "4px",
                          cursor: "pointer",
                          transition: "all 0.3s",
                        }}
                        onClick={() => setCropModalOpen(false)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#007bff";
                          e.currentTarget.style.color = "white";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                          e.currentTarget.style.color = "#007bff";
                        }}
                      >
                        Cancel
                      </button>
                    </Tooltip>
                    <Tooltip title="Confirm your selection" arrow>
                      <button
                        style={{
                          backgroundColor: "#007bff",
                          color: "white",
                          padding: "10px 20px",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          transition: "background-color 0.3s",
                        }}
                        onClick={confirmCrop}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#0056b3")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#007bff")}
                      >
                        Confirm
                      </button>
                    </Tooltip>
                  </div>
                </Box>
              </Modal>
            )}
          </>
          {/* end::Input group */}
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-templates-modal-action='cancel'
            disabled={formik.isSubmitting || isTemplateLoading}
          >
            Discard
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-templates-modal-action='submit'
            disabled={isTemplateLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'>Submit</span>
            {(formik.isSubmitting || isTemplateLoading) && (
              <span className='indicator-progress'>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form >
      {printableTemplatePreview && (
        <div
          className='printable-template-preview-overlay'
          onMouseEnter={clearPrintableTemplatePreviewTimers}
          onMouseLeave={schedulePrintableTemplatePreviewHide}
          style={{
            position: 'fixed',
            top: `${printableTemplatePreview.top}px`,
            left: `${printableTemplatePreview.left}px`,
            transform: 'translate(-50%, -50%)',
            width: '300px',
            maxWidth: '80vw',
            maxHeight: '85vh',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 18px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.08)',
            zIndex: 1800,
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            animation: 'printableTemplatePreviewPopIn 0.22s cubic-bezier(0.2, 0.8, 0.2, 1) forwards',
            pointerEvents: 'auto',
          }}
        >
          <img
            src={printableTemplatePreview.avatar}
            alt={printableTemplatePreview.label}
            style={{
              width: '100%',
              maxHeight: 'calc(85vh - 56px)',
              objectFit: 'contain',
              display: 'block',
              background: '#f5f8fa',
            }}
          />
          <div
            style={{
              padding: '10px 12px',
              borderTop: '1px solid rgba(0,0,0,0.08)',
              color: '#181c32',
              background: 'rgba(255,255,255,0.93)',
              textAlign: 'center',
              fontWeight: 500,
              fontSize: '13px',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
            title={`${printableTemplatePreview.label} (${printableTemplatePreview.value})`}
          >
            {printableTemplatePreview.label}
          </div>
        </div>
      )}
      <style>
        {`
          @keyframes printableTemplatePreviewPopIn {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.92);
              filter: blur(2px);
            }
            100% {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
              filter: blur(0);
            }
          }
        `}
      </style>
      {(formik.isSubmitting || isTemplateLoading) && <TemplatesListLoading />
      }
    </>
  )
}

export { TemplateEditModalForm };

